import { deleteRest, get, post, put } from 'utils/rxfetch'
import { buildURL } from 'utils/urls'

import mockAPI from './connectWisePSA.mock'

const BASE_URL = '/members-api/connectwise-psa/integration'

// Toggle between mock and real API
// Set to true for development without backend
// Set to false when backend is ready
const USE_MOCK_API = false

const realAPI = {
  // Connection management
  testConnection: (credentials) => post(`${BASE_URL}/wizard/connection/test/`, credentials),
  getIntegrationStatus: () => get(`${BASE_URL}/connection/status`),

  // Data fetching
  // getCompanies accepts optional credentials for wizard flow
  // If credentials provided, uses POST with wizard path; otherwise uses GET (saved config)
  getCompanies: (credentials) =>
    credentials
      ? post(`${BASE_URL}/wizard/companies/`, credentials)
      : get(`${BASE_URL}/companies/`),
  // Server-side autocomplete + lazy-browse. Empty q => first page alphabetically.
  // Response is camelCase via CamelCaseJSONRenderer: { data, page, pageSize, hasMore }.
  searchCompanies: ({ q = '', page = 1, pageSize = 25, credentials } = {}) => {
    const url = buildURL(`${BASE_URL}/companies/search/`, { q, page, page_size: pageSize }, true)
    return credentials ? post(url, credentials) : get(url)
  },
  // getAgreements accepts optional credentials for wizard flow
  // If credentials provided, uses POST with wizard path; otherwise uses GET (saved config)
  getAgreements: (companyId, credentials) =>
    credentials
      ? post(`${BASE_URL}/wizard/companies/${companyId}/agreements/`, credentials)
      : get(`${BASE_URL}/companies/${companyId}/agreements/`),
  // getProducts accepts optional credentials for wizard flow
  // If credentials provided, uses POST with wizard path; otherwise uses GET (saved config)
  getProducts: (credentials) =>
    credentials ? post(`${BASE_URL}/wizard/products/`, credentials) : get(`${BASE_URL}/products/`),
  // getServiceBoards accepts optional credentials for wizard flow
  // If credentials provided, uses POST with wizard path; otherwise uses GET (saved config)
  getServiceBoards: (credentials) =>
    credentials
      ? post(`${BASE_URL}/wizard/service/boards/`, credentials)
      : get(`${BASE_URL}/service/boards/`),
  // getStatuses accepts optional credentials for wizard flow
  // If credentials provided, uses POST with wizard path; otherwise uses GET (saved config)
  getStatuses: (boardId, credentials) =>
    credentials
      ? post(`${BASE_URL}/wizard/service/boards/${boardId}/statuses/`, credentials)
      : get(`${BASE_URL}/service/boards/${boardId}/statuses/`),
  // getPriorities accepts optional credentials for wizard flow
  // If credentials provided, uses POST with wizard path; otherwise uses GET (saved config)
  getPriorities: (credentials) =>
    credentials
      ? post(`${BASE_URL}/wizard/service/priorities/`, credentials)
      : get(`${BASE_URL}/service/priorities/`),
  // Get IRONSCALES tenant companies with pagination
  getIronscalesCompanies: (page = 1, pageSize, filteredIds) =>
    get(
      buildURL(
        `${BASE_URL}/companies/ironscales/`,
        {
          page,
          page_size: pageSize,
          filtered_ids: filteredIds,
        },
        true
      )
    ),
  // Get Unmapped IRONSCALES tenant companies with pagination
  getUnmappedIronscalesCompanies: (page = 1, pageSize, filteredIds) =>
    get(
      buildURL(
        `${BASE_URL}/companies/unmapped/`,
        {
          page,
          page_size: pageSize,
          filtered_ids: filteredIds,
        },
        true
      )
    ),
  // Integration management
  getIntegration: () => get(`${BASE_URL}`),
  createIntegration: (payload) => post(`${BASE_URL}/`, payload),
  updateIntegration: (payload) => put(`${BASE_URL}/`, payload),
  deleteIntegration: () => deleteRest(`${BASE_URL}/`),
  enableIntegration: (payload) => post(`${BASE_URL}/lifecycle/enable/`, payload),
  deactivateIntegration: () => post(`${BASE_URL}/lifecycle/deactivate/`),

  // Mappings (these are handled by enable endpoint, but kept for compatibility)
  saveCompanyMappings: (mappings) => put(`${BASE_URL}/mappings/`, { mappings }),
  saveProductMappings: (mappings) => put(`${BASE_URL}/product-mappings/`, { mappings }),
  saveTicketBoardConfig: (config) => put(`${BASE_URL}/ticket-board-config/`, config),
}

// Export the appropriate API based on USE_MOCK_API flag
// To switch to real API, just change USE_MOCK_API to false
const api = USE_MOCK_API ? mockAPI : realAPI

// The arrow function should be named. fix it
export default function connectWisePSAAPI() {
  return api
}

// Also export the flag for components that need to know
export { USE_MOCK_API }
